import { useState, useEffect, useCallback } from 'react';
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { api, SENTIMENT_STYLE } from './api';

function inferSentiment(summary) {
  if (!summary) return 'neutral';
  const s = summary.toLowerCase();
  if (/(termin|positiv|interess|zusag|gewonnen|begeist|erfreu|freu)/.test(s)) return 'positiv';
  if (/(absag|kein interesse|nicht passen|verloren|abgelehnt|negativ)/.test(s)) return 'negativ';
  if (/(rückfrag|warte|offen|frag|unklar|prüf)/.test(s)) return 'rueckfrage';
  return 'neutral';
}

export default function AISummary({ leadId, refreshKey, onLoaded }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api(`/api/leads/${leadId}/conversation-summary`);
      setSummary(data);
      onLoaded?.(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [leadId, onLoaded]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const sentiment = inferSentiment(summary?.summary);
  const senStyle = SENTIMENT_STYLE[sentiment];

  const firstLine = (summary?.summary || '').split(/[.\n]/)[0]?.trim() || '';
  const recommendation = summary?.next_step;

  return (
    <div className="border border-[var(--border-card)] rounded-xl bg-[var(--bg-card)] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-inset)] transition-colors text-left"
      >
        <span className={`w-2.5 h-2.5 rounded-full ${senStyle.dot} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          {loading ? (
            <span className="text-[13px] text-[var(--text-tertiary)] flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI-Analyse läuft …
            </span>
          ) : error ? (
            <span className="text-[13px] text-red-600">Fehler: {error}</span>
          ) : summary ? (
            <>
              <div className="text-[13px] font-medium text-[var(--text-primary)] truncate">
                {senStyle.label} · {firstLine || 'Keine Zusammenfassung'}
              </div>
              {recommendation && (
                <div className="text-[12px] text-[var(--text-secondary)] mt-0.5 truncate">
                  💡 {recommendation}
                </div>
              )}
            </>
          ) : null}
        </div>
        <Sparkles className="w-4 h-4 text-[#338edc] flex-shrink-0" />
        {expanded ? <ChevronUp className="w-4 h-4 text-[var(--text-tertiary)]" /> : <ChevronDown className="w-4 h-4 text-[var(--text-tertiary)]" />}
      </button>

      {expanded && (
        <div className="border-t border-[var(--border-card)] px-4 py-3 bg-[var(--bg-inset)]/50">
          {summary?.summary ? (
            <div className="text-[13px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">
              {summary.summary}
            </div>
          ) : (
            <div className="text-[13px] text-[var(--text-tertiary)] italic">Keine Daten</div>
          )}
          <div className="flex items-center gap-3 mt-3 text-[11px] text-[var(--text-tertiary)]">
            <span>{summary?.email_count ?? 0} E-Mails</span>
            {summary?.unread_count > 0 && <span className="text-[#338edc] font-medium">{summary.unread_count} ungelesen</span>}
            {summary?.days_since_last_activity != null && (
              <span>Letzte Aktivität: vor {summary.days_since_last_activity} Tag{summary.days_since_last_activity === 1 ? '' : 'en'}</span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); load(); }}
              disabled={loading}
              className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-[var(--bg-card)] text-[var(--text-secondary)] disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
